// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  useConnectionMutationBridge,
  useEntityFieldsBridge,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {useQuery} from '@github-ui/react-query'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {clientSideRelayFetchQueryRetainedWithCache} from '@github-ui/relay-query-cache/query-helpers'
import {useEffect, useMemo, useState} from 'react'
import type {Environment} from 'react-relay'
import {useFragment, useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, graphql} from 'relay-runtime'

import type {
  IssueViewerSecondaryViewQuery,
  IssueViewerSecondaryViewQuery$data,
} from './__generated__/IssueViewerSecondaryViewQuery.graphql'
import type {IssueViewerSecondaryViewQueryData$key} from './__generated__/IssueViewerSecondaryViewQueryData.graphql'
import {
  fetchIssueViewerSecondaryView,
  type IssueViewerLiveUpdateChannels,
  issueViewerSecondaryViewQueryKey,
  type IssueViewerSecondaryViewResponse,
} from './fetch-issue-viewer-secondary-view'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'

const SUB_ISSUES_CONNECTION_PATH = ['subIssues']

type UseSecondaryQueryProps = {
  owner: string
  repo: string
  number: number
  markAsRead?: boolean
}

export function resolveHideClosedSubIssuesEnabled(
  serverFeatures: IssueViewerSecondaryViewResponse['features'] | undefined,
  clientFeatureEnabled: boolean,
): boolean {
  return serverFeatures?.hideClosedSubIssues ?? clientFeatureEnabled
}

// When variables are added to this query, places that add the preload header for ISSUE_VIEWER_SECONDARY_VIEW_QUERY
// also need to be updated accordingly to ensure the queries match.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueViewerSecondaryGraphqlQuery = graphql`
  query IssueViewerSecondaryViewQuery($repo: String!, $owner: String!, $number: Int!, $markAsRead: Boolean = false) {
    repository(name: $repo, owner: $owner) {
      id
      ...IssueViewerSecondaryViewQueryData @arguments(number: $number, markAsRead: $markAsRead)
    }
  }
`

// This defines all of the data fetched in the secondary query.
// Fields formerly behind the separate `IssueViewerSecondaryIssueData` fragment are now inlined directly
// here so `secondaryData.issue` can be read as plain props (no nested `useFragment`/masking). Duplicate
// selections that existed across the old fragment (e.g. multiple `repository`/`parent`/`issueDependenciesSummary`
// blocks) have been merged into a single selection per field.
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueViewerSecondaryData = graphql`
  fragment IssueViewerSecondaryViewQueryData on Repository
  @argumentDefinitions(number: {type: "Int!"}, markAsRead: {type: "Boolean", defaultValue: true}) {
    issue(number: $number, markAsRead: $markAsRead) {
      id
      isTransferInProgress
      parent {
        id
        number
        repository {
          name
          nameWithOwner
          owner {
            login
          }
        }
        state
        stateReason
        title
        titleHTML
        url
        subIssuesSummary {
          total
          completed
        }
      }
      viewerCanReopen
      viewerCanClose
      authorAssociation
      viewerThreadSubscriptionFormAction
      reactionGroups {
        content
        viewerHasReacted
      }
      # Also includes fields inlined from the former IssueSidebarLazySections fragment, read as plain
      # props by IssueSidebarLazySections, which forwards slices to its already-migrated child sections.
      repository {
        id
        databaseId
        isLocked
        isWritable
        viewerCanPush
        slashCommandsEnabled
        codeOfConductFileUrl
        securityPolicyUrl
        contributingFileUrl
        supportFileUrl
        name
        nameWithOwner
        isArchived
        viewerCanPinIssues
        viewerCanCreateIssues
        isPrivate
        visibility
        owner {
          login
        }
      }
      suggestedActors(first: 10) {
        nodes {
          ... on Actor {
            __typename
            id
            login
            name
            profileResourcePath
            avatarUrl(size: 64)
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
      }
      projectItems(first: 10) @connection(key: "ProjectSection_projectItems") {
        edges {
          node {
            project {
              id
              title
              closed
              number
              url
              viewerCanUpdate
              hasReachedItemsLimit
            }
          }
        }
      }
      milestone {
        id
        title
        closed
        dueOn
        progressPercentage
        url
        closedAt
      }
      title
      number
      databaseId
      linkedBranches(first: 25) {
        nodes {
          id
          ref {
            name
            id
            __typename
            target {
              oid
              id
            }
            repository {
              id
              nameWithOwner
              defaultBranchRef {
                name
                id
                target {
                  oid
                  id
                }
                associatedPullRequests {
                  totalCount
                }
              }
            }
            associatedPullRequests {
              totalCount
            }
          }
        }
      }
      closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
        nodes {
          ... on PullRequest {
            id
            url
            number
            title
            state
            isDraft
            isInMergeQueue
            createdAt
            databaseId
            repository {
              id
              name
              nameWithOwner
              owner {
                id
                login
                __typename
              }
            }
            __typename
          }
        }
      }
      linkedPullRequestReleases(first: 10) {
        nodes {
          pullRequest {
            id
          }
          release {
            id
            tagName
            name
            url
            isPrerelease
            isLatest
          }
        }
      }
      viewerCanLinkBranches
      viewerCanUpdateMetadata
      topBlockedBy: blockedBy(first: 3, ranked: true) {
        nodes {
          id
          title
          titleHTML
          url
          number
          repository {
            nameWithOwner
            name
            owner {
              login
            }
          }
          state
          stateReason
        }
        pageInfo {
          hasNextPage
        }
      }
      topBlocking: blocking(first: 3, ranked: true) {
        nodes {
          id
          title
          titleHTML
          url
          number
          repository {
            nameWithOwner
            name
            owner {
              login
            }
          }
          state
          stateReason
        }
        pageInfo {
          hasNextPage
        }
      }
      topRelatesTo: relatesTo(first: 3, ranked: true) {
        totalCount
        nodes {
          id
          title
          titleHTML
          url
          number
          repository {
            nameWithOwner
            name
            owner {
              login
            }
          }
          state
          stateReason
        }
        pageInfo {
          hasNextPage
        }
      }
      securityAlerts(first: 100) {
        nodes {
          number
          repository {
            nameWithOwner
            name
            owner {
              login
            }
          }
          status
          title
        }
      }
      issueDependenciesSummary {
        blockedBy
        blocking
      }
      threadSubscriptionChannel
      viewerCustomSubscriptionEvents
      participants(first: 10) {
        nodes {
          login
          avatarUrl(size: 64)
          id
        }
        totalCount
      }
      viewerCanConvertToDiscussion
      viewerCanDelete
      viewerCanTransfer
      viewerCanType
      viewerCanLock
      stateReason
      url
      issueType {
        name
        color
      }
      subIssuesSummary {
        total
        completed
      }
      agentAssignments {
        __typename
      }
      trackedInIssues(first: 10) {
        nodes {
          number
          url
          stateReason
        }
        totalCount
      }
      viewerCanReadUserContentEdits
      lastEditedAt
      lastUserContentEdit {
        editor {
          url
          login
        }
      }
      showSpammyBadge
      viaApp {
        name
        slug
        url
      }
      viewerCanReport
      viewerCanReportToMaintainer
      viewerCanBlockFromOrg
      viewerCanUnblockFromOrg
      viewerCanBlockPersonally
      viewerCanUnblockPersonally
      pinnedIssueComment {
        id
        issueComment {
          id
          showSpammyBadge
        }
      }
      # sub-issues
      subIssuesConnection: subIssues {
        # Important to use totalCount (not subIssuesSummary.total) so we use the same field everywhere, keeping in sync when mutating
        totalCount
      }
      subIssues(first: 100) {
        nodes {
          id
          closed
          state
          stateReason
          issueDependenciesSummary {
            blockedBy
          }
          assignees(first: 10) {
            totalCount
            edges {
              node {
                id
                login
                avatarUrl
              }
            }
          }
          url
          repository {
            name
            owner {
              login
            }
          }
          databaseId
          number
          title
          titleHTML
          issueType {
            id
            name
            color
          }
          subIssuesSummary {
            total
            completed
          }
          closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
            totalCount
          }
        }
      }
      state
      blockedBy(first: 1, ranked: true) {
        nodes {
          titleHTML
          number
          url
          repository {
            name
            owner {
              login
            }
          }
        }
      }
      discussion {
        url
      }
    }
  }
`

function markIssueRead(environment: Environment, issueId: string) {
  return commitLocalUpdate(environment, store => {
    const issue = store.get(issueId)
    if (issue) {
      issue.setValue(true, 'isReadByViewer')
    }
  })
}

export function useSecondaryQuery({
  owner,
  repo,
  number,
  markAsRead = true,
}: UseSecondaryQueryProps): [
  IssueSecondaryNode | null | undefined,
  boolean,
  IssueViewerLiveUpdateChannels | undefined,
] {
  const tanstackSecondaryViewEnabled = isFeatureEnabled('issue_viewer_secondary_view_tanstack')
  const clientHideClosedSubIssuesEnabled = isFeatureEnabled('issues_hide_closed_sub_issues')
  const environment = useRelayEnvironment()
  const [secondaryQueryData, setSecondaryQueryData] = useState<IssueViewerSecondaryViewQueryData$key | null>(null)

  useEffect(() => {
    // Skip the Relay network fetch entirely once the TanStack path is enabled, so we don't
    // do the work twice.
    if (tanstackSecondaryViewEnabled) return

    clientSideRelayFetchQueryRetainedWithCache<IssueViewerSecondaryViewQuery>(
      {
        environment,
        query: IssueViewerSecondaryGraphqlQuery,
        variables: {
          owner,
          repo,
          number,
          markAsRead,
        },
      },
      DEFAULT_RELAY_CACHE_TTL,
      ['markAsRead'],
    ).subscribe({
      next: (data: IssueViewerSecondaryViewQuery$data) => {
        setSecondaryQueryData(data.repository ?? null)
      },
    })
  }, [tanstackSecondaryViewEnabled, environment, markAsRead, number, owner, repo])

  const secondaryData = useFragment(IssueViewerSecondaryData, secondaryQueryData)

  // RELAY-CLEANUP(IssueViewerSecondaryViewQuery): `secondaryData.issue` no longer goes through a nested
  // fragment/mask (fields are inlined above), but it's still read via `useFragment`, so its generated
  // type carries Relay's open-enum (`%future added value`) variants that the allowlisted
  // `IssueViewerSecondaryViewQuery` SPQ type omits. Cast at this transition boundary; remove once
  // IssueViewerSecondaryViewQuery leaves Relay.
  const relaySecondaryIssueData = secondaryData?.issue as IssueSecondaryNode | null | undefined

  // markAsRead is excluded from the query key because it triggers a server side effect
  // (clearing the unread notification); including it would let a cached response satisfy
  // the request, so the notification would never get marked as read.
  const secondaryViewQueryKey = issueViewerSecondaryViewQueryKey(owner, repo, number)
  const {data: tanstackSecondaryViewData} = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- markAsRead intentionally excluded from key (see above)
    queryKey: secondaryViewQueryKey,
    queryFn: ({signal}) => fetchIssueViewerSecondaryView(owner, repo, number, markAsRead, signal),
    enabled: tanstackSecondaryViewEnabled,
  })

  // Entity field edits arrive as operations naming their own target, so the overlay finds every copy
  // this document holds — the issue, its parent, its sub-issue rows, its milestone.
  useEntityFieldsBridge()

  // Overlay in-flight sub-issue connection mutations onto this query's data at read time. Gated on
  // a resolved issue id too, so the overlay stays a disabled no-op until the secondary view loads.
  const overlayIssueId = tanstackSecondaryViewData?.repository?.issue?.id
  const optimisticOverlayEnabled =
    tanstackSecondaryViewEnabled &&
    isFeatureEnabled('issue_viewer_subissues_optimistic_overlay') &&
    overlayIssueId != null
  // Owner id points at `repository.issue`; the engine finds it by id anywhere in `base`, so the
  // connection path stays `['subIssues']`. `?? ''` only covers disabled passes (never a live owner).
  const subIssuesOwnerId = overlayIssueId ?? ''

  // Mirrors every `commitRemoveSubIssueMutation` into the mutation cache for the overlay below.
  useConnectionMutationBridge({
    ownerId: subIssuesOwnerId,
    connectionPath: SUB_ISSUES_CONNECTION_PATH,
    enabled: optimisticOverlayEnabled,
  })

  // Opt-in per connection: insert positions and count deltas only mean something where it renders.
  const optimisticConnections = useMemo(
    () => (optimisticOverlayEnabled ? [{ownerId: subIssuesOwnerId, connectionPath: SUB_ISSUES_CONNECTION_PATH}] : []),
    [optimisticOverlayEnabled, subIssuesOwnerId],
  )
  // No `revalidate`: `SubIssue::MAXIMUM_BREADTH` caps a parent at 100, so `subIssues(first: 100)` is
  // never a partial page and the fold over the remove operation's deltas is already authoritative.
  const secondaryViewTargets = useMemo(() => [{queryKey: secondaryViewQueryKey}], [secondaryViewQueryKey])
  const mergedSecondaryViewData = useOptimisticData(tanstackSecondaryViewData, {
    // Not the overlay flag: `optimisticConnections` is empty until that is on, and entity
    // operations need no connection list.
    enabled: tanstackSecondaryViewEnabled,
    connections: optimisticConnections,
    targets: secondaryViewTargets,
  })

  // The server-persisted query's generated type doesn't carry Relay's synthetic `__isActor`/
  // `__isNode` type-refinement markers on the `suggestedActors` union (those have no real
  // GraphQL/network equivalent), so it's not structurally identical to `IssueSecondaryNode`.
  // Cast at this transition boundary, same as the Relay path above.
  const tanstackSecondaryIssueData = mergedSecondaryViewData?.repository?.issue as IssueSecondaryNode | null | undefined

  const secondaryIssueData = tanstackSecondaryViewEnabled ? tanstackSecondaryIssueData : relaySecondaryIssueData
  const hideClosedSubIssuesEnabled = resolveHideClosedSubIssuesEnabled(
    tanstackSecondaryViewData?.features,
    clientHideClosedSubIssuesEnabled,
  )
  const issueId = secondaryIssueData && secondaryIssueData.id

  useEffect(() => {
    if (markAsRead && issueId) {
      markIssueRead(environment, issueId)
    }
  }, [environment, markAsRead, issueId])

  const liveUpdateChannels = tanstackSecondaryViewEnabled ? tanstackSecondaryViewData?.channels : undefined

  return [secondaryIssueData, hideClosedSubIssuesEnabled, liveUpdateChannels]
}
